import { memoize } from "lodash";

import type { Vehicle } from "@busbud/int-schemas/schemas/search/vehicle";
import type * as UDM from "@busbud/int-schemas/udm";

import { getSeatById } from "@app/helpers/udm/vehicle";
import type {
  AssignedSeat,
  SeatSelectionStateValue
} from "@app/types/checkout-types";
import type { NapiDepartureCart } from "@app/types/napi-departures-types";
import type {
  BusLayout,
  BusLayoutCell,
  BusLayoutCellGender,
  BusLayoutFloor,
  NonEmptyVehicleLayoutCell
} from "@app/types/napi-layout-types";

import { FEMALE, MALE } from "../../../constants/passengers";

function _cellHasPrices(cell: BusLayoutCell | UDM.SeatCell | null): boolean {
  return Boolean(
    cell &&
      cell.type !== "empty" &&
      cell.prices &&
      Object.keys(cell.prices).length
  );
}

// TODO-TRAIN-SUPPORT: adjust type when train become supported.
export function vehicleHasGenderedSeats(vehicle: BusLayout | null): boolean {
  if (!vehicle) {
    return false;
  }

  const layout = vehicle.floors || vehicle.layout;
  if (!layout) {
    return false;
  }

  const has_gendered_seats_rule =
    vehicle.rules &&
    (vehicle.rules.hasOwnProperty("max_number_of_gendered_seat") ||
      vehicle.rules.hasOwnProperty("gendered_seat"));

  const has_gendered_seats_cell = layout.some(floor =>
    floor.cells.some(rows =>
      rows.some(
        cell =>
          cell.hasOwnProperty("gender") && cell.type === "seat" && !!cell.gender
      )
    )
  );

  return has_gendered_seats_rule || has_gendered_seats_cell;
}

function _seatSelectionHasGenders(
  departures: Pick<NapiDepartureCart, "vehicle" | "id">[]
): boolean {
  return departures.some(departure =>
    vehicleHasGenderedSeats(departure.vehicle)
  );
}

export const seatSelectionHasGenders = memoize(
  _seatSelectionHasGenders,
  departures => departures.map(({ id }: { id: string }) => id).join("-")
);

/**
 * Determines if the seat selection action should be async and reload the cart.
 * Returns true if the selected or unselected seat has prices.
 *
 * @param departure_id
 * @param passenger_index
 * @param new_seat
 * @param previous_seats
 */
export function seatSelectionShouldCallNapi(
  departure_id: string,
  passenger_index: number,
  new_seat: BusLayoutCell | null,
  previous_seats: SeatSelectionStateValue
): boolean {
  // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  const prev_seat = previous_seats[departure_id].seats[passenger_index];

  // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  return _cellHasPrices(new_seat) || _cellHasPrices(prev_seat);
}

type PassengersSeatSelectedHasPricesParams = {
  vehicle: UDM.Vehicle;
  new_seats_selected_id: (string | undefined)[];
  previous_seats_selected_id: (string | undefined)[];
};

function passengersSeatsSelectedHasPrices({
  vehicle,
  new_seats_selected_id,
  previous_seats_selected_id
}: PassengersSeatSelectedHasPricesParams): boolean {
  const new_cells = new_seats_selected_id.map(seat_id => {
    if (!seat_id) {
      return null;
    }

    return getSeatById(vehicle, seat_id);
  });

  const previous_cells = previous_seats_selected_id.map(seat_id => {
    if (!seat_id) {
      return null;
    }

    return getSeatById(vehicle, seat_id);
  });

  return (
    new_cells.some(cell => _cellHasPrices(cell)) ||
    previous_cells.some(cell => _cellHasPrices(cell))
  );
}

export const memoizedPassengersSeatsSelectedHasPrices = memoize(
  passengersSeatsSelectedHasPrices,
  ({ new_seats_selected_id, previous_seats_selected_id }) =>
    previous_seats_selected_id.concat(new_seats_selected_id).join()
);

export function getAvailableGenderForSeat(
  _departure_id: string,
  seat: NonEmptyVehicleLayoutCell,
  floors: Array<BusLayoutFloor>,
  assigned_seats: Array<AssignedSeat>,
  max_number_of_gendered_seat:
    | Vehicle.VehicleRules["max_number_of_gendered_seat"]
    | null
): BusLayoutCellGender {
  if (seat.gender && seat.gender !== "any") {
    return seat.gender;
  } else if (!isNextToAnotherSeat(_departure_id, seat.id, floors)) {
    return "any";
  } else if (max_number_of_gendered_seat) {
    const genders = [FEMALE, MALE] as const;

    for (const gender of genders) {
      const max_seats = max_number_of_gendered_seat[gender];
      if (max_seats) {
        const nb_of_restricted_seat = getNumberOfRestrictedSeatsForGender(
          _departure_id,
          gender,
          floors
        );
        if (nb_of_restricted_seat >= max_seats) {
          return gender === FEMALE ? MALE : FEMALE;
        }
      }
    }
  }

  const adjacent_assigned_seat = getAdjacentAssignedSeat(
    _departure_id,
    seat.id,
    assigned_seats,
    floors
  );
  if (adjacent_assigned_seat && adjacent_assigned_seat.gender) {
    return adjacent_assigned_seat.gender;
  }

  return "any";
}

export function getNumberOfRestrictedSeatsForGender(
  _departure_id: string,
  gender: BusLayoutCellGender,
  floors: Array<BusLayoutFloor>
): number {
  const seats = getAllSeatsFromFloors(_departure_id, floors);
  return seats.filter(seat => seat.gender === gender && seat.availability)
    .length;
}

export function getAdjacentAssignedSeat(
  _departure_id: string,
  seat_id: string,
  assigned_seats: Array<AssignedSeat>,
  floors: Array<BusLayoutFloor>
): AssignedSeat | null {
  const row = getRowFromSeatId(_departure_id, seat_id, floors);
  if (row) {
    for (const [cell_index, cell] of row.entries()) {
      if (cell.type !== "empty" && cell.id === seat_id) {
        for (const assigned_seat of assigned_seats) {
          if (assigned_seat.seat.type !== "empty") {
            const previous_cell = row[cell_index - 1];
            const next_cell = row[cell_index + 1];

            if (
              (previous_cell &&
                previous_cell.type !== "empty" &&
                previous_cell.id === assigned_seat.seat.id) ||
              (next_cell &&
                next_cell.type !== "empty" &&
                next_cell.id === assigned_seat.seat.id)
            ) {
              return assigned_seat;
            }
          }
        }
      }
    }
  }
  return null;
}

export function isNextToAnotherSeat(
  _departure_id: string,
  seat_id: string,
  floors: Array<BusLayoutFloor>
): boolean {
  const row = getRowFromSeatId(_departure_id, seat_id, floors);
  if (row) {
    for (const [cell_index, cell] of row.entries()) {
      if (cell.type !== "empty" && cell.id === seat_id) {
        // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        return (
          // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
          (row[cell_index - 1] && row[cell_index - 1].type === "seat") ||
          // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
          (row[cell_index + 1] && row[cell_index + 1].type === "seat")
        );
      }
    }
  }
  return false;
}

export function areSeatsAdjacent(
  _departure_id: string,
  seat_a_id: string,
  seat_b_id: string,
  floors: Array<BusLayoutFloor>
): boolean {
  const row = getRowFromSeatId(_departure_id, seat_a_id, floors);
  if (row) {
    for (const [cell_index, cell] of row.entries()) {
      if (cell.type !== "empty" && cell.id === seat_a_id) {
        const previous_cell = row[cell_index - 1];
        const next_cell = row[cell_index + 1];

        if (
          (previous_cell &&
            previous_cell.type !== "empty" &&
            previous_cell.id === seat_b_id) ||
          (next_cell &&
            next_cell.type !== "empty" &&
            next_cell.id === seat_b_id)
        ) {
          return true;
        }
      }
    }
  }
  return false;
}

function _getAllSeatsFromFloors(
  _departure_id: string,
  floors: Array<BusLayoutFloor>
): Array<NonEmptyVehicleLayoutCell> {
  const seats: Array<NonEmptyVehicleLayoutCell> = [];
  floors.forEach(floor => {
    floor.cells.forEach(row => {
      row.forEach(cell => {
        if (cell.type === "seat") {
          seats.push(cell);
        }
      });
    });
  });
  return seats;
}
export const getAllSeatsFromFloors = memoize(_getAllSeatsFromFloors);

export function getRowFromSeatId(
  _departure_id: string,
  seat_id: string,
  floors: Array<BusLayoutFloor>
): Array<BusLayoutCell> {
  const { row } = getContainersFromSeatId(_departure_id, seat_id, floors);
  return row;
}

export function getFloorFromSeatId(
  _departure_id: string,
  seat_id: string,
  floors: Array<BusLayoutFloor>
): BusLayoutFloor | null {
  const { floor } = getContainersFromSeatId(_departure_id, seat_id, floors);
  return floor;
}

function _getContainersFromSeatId(
  _departure_id: string,
  seat_id: string,
  floors: Array<BusLayoutFloor>
): { floor: BusLayoutFloor | null; row: Array<BusLayoutCell> } {
  for (const floor of floors) {
    for (const row of floor.cells) {
      for (const cell of row) {
        if (cell.type !== "empty" && cell.id === seat_id) {
          return { floor, row };
        }
      }
    }
  }
  return { floor: null, row: [] };
}

export const getContainersFromSeatId = memoize(
  _getContainersFromSeatId,
  (_departure_id, seat_id) => [_departure_id, seat_id].join("-")
);
